import { useCoAgent } from '@copilotkit/react-core'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { z } from 'zod'

import {
  OneEllipsis,
  F0Box,
  F0Button,
  F0Form,
  F0Icon,
  useF0AiFormRegistry,
  useF0Form,
  useF0FormDefinition,
  useFormComponent,
  type F0FormSchema,
  type ModuleId,
} from '@factorialco/f0-react'
import { CheckCircle } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

// Loose alias for F0Form variants used in the canvas — replicates f0's
// internal F0FormLikeComponent without requiring it to be public.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CanvasFormComponent = (props: any) => ReactNode

function useCanvasFormComponent(): CanvasFormComponent {
  const FormComponent = useFormComponent()
  // eslint-disable-next-line no-type-assertion/no-type-assertion -- match upstream cast
  return (FormComponent ?? F0Form) as CanvasFormComponent
}

interface CoAgentFormState {
  activeForm?: {
    formName: string
    description?: string
    module?: ModuleId
    formValues?: Record<string, unknown>
    defaultValuesParams?: Record<string, unknown>
  } | null
}

// eslint-disable-next-line no-type-assertion/no-type-assertion -- zod object literal cast for schema fallback
const FALLBACK_SCHEMA = z.object({}) as unknown as F0FormSchema

// ---------- Submit success overlay ----------

function SubmitSuccessOverlay() {
  return (
    <F0Box
      data-testid="canvas-form-success"
      position="absolute"
      top="none"
      right="none"
      bottom="none"
      left="none"
      zIndex="10"
      display="flex"
      alignItems="center"
      justifyContent="center"
      background="primary"
    >
      <F0Box display="flex" flexDirection="column" alignItems="center" gap="md">
        <F0Icon icon={CheckCircle} className="h-8 w-8 text-f1-icon-positive" />
        <OneEllipsis className="text-base font-medium text-f1-foreground">
          {i18n.t('common.ai.form_card.saved')}
        </OneEllipsis>
      </F0Box>
    </F0Box>
  )
}

// ---------- Wizard canvas content ----------

function WizardCanvasContent({
  formDefinition,
  formRef,
  isSubmitting,
  hasErrors,
  onSubmit,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formDefinition: any
  formRef: ReturnType<typeof useF0Form>['formRef']
  isSubmitting: boolean
  hasErrors: boolean
  onSubmit: () => void
}) {
  const errorTriggerMode = formDefinition.errorTriggerMode ?? 'on-blur'
  const CanvasForm = useCanvasFormComponent()

  return (
    <F0Box display="flex" height="full" flexDirection="column">
      {/* Plain div: needs the `[&>div]:h-full` child selector, which F0Box can't express. */}
      <div className="relative min-h-0 flex-1 overflow-hidden [&>div]:h-full">
        {isSubmitting && <SubmitSuccessOverlay />}
        <CanvasForm
          formDefinition={formDefinition}
          styling={{ showSectionsSidepanel: true }}
          formRef={formRef}
        />
      </div>
      {!isSubmitting && (
        <F0Box
          data-testid="canvas-form-footer"
          display="flex"
          alignItems="center"
          justifyContent="end"
          gap="md"
          shrink={false}
          border="none"
          borderTop="default"
          borderColor="secondary"
          background="primary"
          paddingX="xl"
          paddingY="md"
        >
          <F0Button
            variant="default"
            onClick={onSubmit}
            disabled={hasErrors && errorTriggerMode !== 'on-submit'}
            label={formDefinition.submitConfig?.label ?? i18n.t('common.submit')}
          />
        </F0Box>
      )}
    </F0Box>
  )
}

// ---------- Plain form content (no steps) ----------

function PlainFormContent({
  formDefinition,
  formRef,
  isSubmitting,
  hasErrors,
  onSubmit,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formDefinition: any
  formRef: ReturnType<typeof useF0Form>['formRef']
  isSubmitting: boolean
  hasErrors: boolean
  onSubmit: () => void
}) {
  const errorTriggerMode = formDefinition.errorTriggerMode ?? 'on-blur'
  const CanvasForm = useCanvasFormComponent()
  const showSectionsSidepanel = !!(
    formDefinition.sections && Object.keys(formDefinition.sections).length > 2
  )

  return (
    <F0Box display="flex" height="full" flexDirection="column">
      {/* Plain div: the `[&>div]:h-full` child selector variant has no F0Box equivalent. */}
      <div
        className={`relative min-h-0 flex-1 ${
          showSectionsSidepanel ? 'overflow-hidden [&>div]:h-full' : 'overflow-auto p-6 pb-6'
        }`}
      >
        {isSubmitting && <SubmitSuccessOverlay />}
        <CanvasForm
          formDefinition={formDefinition}
          formRef={formRef}
          styling={{ showSectionsSidepanel }}
        />
      </div>
      {!isSubmitting && (
        <F0Box
          data-testid="canvas-form-footer"
          display="flex"
          alignItems="center"
          justifyContent="end"
          gap="md"
          shrink={false}
          border="none"
          borderTop="default"
          borderColor="secondary"
          background="primary"
          paddingX="xl"
          paddingY="md"
        >
          <F0Button
            variant="default"
            onClick={onSubmit}
            disabled={hasErrors && errorTriggerMode !== 'on-submit'}
            label={formDefinition.submitConfig?.label ?? i18n.t('common.submit')}
          />
        </F0Box>
      )}
    </F0Box>
  )
}

// ---------- Virtual form content (router) ----------

export type FormCanvasContentProps = {
  /**
   * Mastra co-agent name to subscribe to. The host (entity definition)
   * derives it from `useAiChat().agent` and forwards it as a prop so
   * this component stays decoupled from the F0AiChat context.
   */
  agent: string
  /**
   * Called when the form submits successfully (after the success animation).
   * Typically wired to `useAiChat().closeCanvas` upstream.
   */
  onSubmitComplete: () => void
}

function VirtualFormContent({ agent, onSubmitComplete }: FormCanvasContentProps) {
  const { formRef, hasErrors } = useF0Form()
  const registry = useF0AiFormRegistry()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }
  }, [])

  const { state } = useCoAgent<CoAgentFormState>({ name: agent })

  const activeForm = state?.activeForm
  const formName = activeForm?.formName ?? ''
  const entry = formName ? registry?.get(formName) : undefined
  const ref = entry?.ref.current
  const currentValues = activeForm?.formValues ?? {}
  // Prefer params from the registry entry (written by the host-app form
  // actions) as the canonical source; fall back to coagent state for the
  // brief window before sync.
  const defaultValuesParams = entry?.defaultValuesParams ?? activeForm?.defaultValuesParams

  // Keep refs so callbacks always capture the latest values without
  // changing identity (which would cause useF0FormDefinition to recompute).
  const entryRef = useRef(entry)
  entryRef.current = entry
  const defaultValuesParamsRef = useRef(defaultValuesParams)
  defaultValuesParamsRef.current = defaultValuesParams
  const currentValuesRef = useRef(currentValues)
  currentValuesRef.current = currentValues
  const registryRef = useRef(registry)
  registryRef.current = registry
  const formNameRef = useRef(formName)
  formNameRef.current = formName
  const onSubmitCompleteRef = useRef(onSubmitComplete)
  onSubmitCompleteRef.current = onSubmitComplete

  // Only apply values once when the canvas first opens for this form.
  // Re-applying formValues continuously from coagent state would loop:
  // form renders → rebuildDescriptions → coagent sync → setValues → re-render.
  const initializedFormRef = useRef('')
  useEffect(() => {
    if (!ref || !formName) return
    if (initializedFormRef.current === formName) return
    initializedFormRef.current = formName
    const valuesToApply = registry?.getFillVersion?.(formName)
      ? (entry?.ref.current?.getValues() ?? currentValues)
      : currentValues
    const existing = ref.getValues()
    if (JSON.stringify(existing) === JSON.stringify(valuesToApply)) return
    ref.setValues(valuesToApply, { shouldDirty: true, shouldValidate: false })
  }, [ref, formName, JSON.stringify(currentValues)])

  const handleSubmit = useCallback(() => {
    formRef.current?.submit().catch(() => {
      // Validation errors are displayed by the form
    })
  }, [formRef])

  const resolvedDefaultValues = useCallback(() => {
    const e = entryRef.current
    const name = formNameRef.current
    const params = defaultValuesParamsRef.current

    const paramsKey = params ? JSON.stringify(params) : null
    if (name && registryRef.current?.hasDefaultValuesEverResolved?.(name, paramsKey)) {
      return Promise.resolve(e?.ref.current?.getValues() ?? currentValuesRef.current)
    }

    if (e?.defaultValuesFn && params) {
      const virtualValues = e.ref.current?.getValues() ?? {}
      const fallbackValues = { ...virtualValues }
      const dirty = e.dirtyFields ?? new Set<string>()
      if (name) registryRef.current?.markDefaultValuesResolving?.(name)
      return e
        .defaultValuesFn(params)
        .then((values) => {
          const merged = { ...values }
          for (const field of dirty) {
            if (field in virtualValues) {
              merged[field] = virtualValues[field]
            }
          }
          return merged
        })
        .catch(() => fallbackValues)
        .finally(() => {
          if (name) registryRef.current?.markDefaultValuesResolved?.(name, paramsKey)
        })
    }
    return Promise.resolve(currentValuesRef.current)
  }, [])

  const onSubmit = useCallback(async ({ data }: { data: Record<string, unknown> }) => {
    setIsSubmitting(true)
    await entryRef.current?.onSubmit?.(data)
    closeTimerRef.current = setTimeout(() => {
      registryRef.current?.resetFillVersion(formNameRef.current)
      registryRef.current?.clearActiveForm()
      onSubmitCompleteRef.current()
    }, 1500)
    return { success: true }
  }, [])

  const formDefinition = useF0FormDefinition({
    name: formName || 'form',
    schema: entry?.schema ?? FALLBACK_SCHEMA,
    defaultValues: resolvedDefaultValues,
    sections: entry?.sections,
    steps: entry?.steps,
    module: entry?.module,
    description: entry?.description,
    errorTriggerMode: entry?.errorTriggerMode,
    submitConfig: {
      type: 'default',
      hideSubmitButton: true,
      hideActionBar: true,
      ...(entry?.submitConfig?.label && { label: entry.submitConfig.label }),
    },
    onSubmit,
  })

  if (!activeForm || !entry) return null

  if (formDefinition.steps?.length) {
    return (
      <WizardCanvasContent
        formDefinition={formDefinition}
        formRef={formRef}
        isSubmitting={isSubmitting}
        hasErrors={hasErrors}
        onSubmit={handleSubmit}
      />
    )
  }

  return (
    <PlainFormContent
      formDefinition={formDefinition}
      formRef={formRef}
      isSubmitting={isSubmitting}
      hasErrors={hasErrors}
      onSubmit={handleSubmit}
    />
  )
}

/**
 * Canvas panel content for forms.
 * Reads the active form from coagent shared state and delegates to
 * VirtualFormContent for rendering. Decoupled from F0AiChat — receives
 * `agent` and `onSubmitComplete` via props.
 */
export function FormCanvasContent({ agent, onSubmitComplete }: FormCanvasContentProps): ReactNode {
  const { state } = useCoAgent<CoAgentFormState>({ name: agent })

  const formName = state?.activeForm?.formName

  if (!formName) return null

  return (
    <F0Box marginX="auto" height="full">
      <VirtualFormContent agent={agent} onSubmitComplete={onSubmitComplete} />
    </F0Box>
  )
}
